import React, { useMemo } from 'react';

import {
  Alert,
  Box,
  Checkbox,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useGetTourOfferDepartures } from '~/hooks/apis/tours/useTourOffers';

import { formatDateWeekDayShortMonth } from '~/services/TimeService';

interface Props {
  tourOfferId: string;
  selected: Array<string>;
  onChange: (dates: Array<string>) => void;
  /** Dates already saved on the bundle; any the tour no longer departs on stay listed so they can be unticked. */
  savedDates?: Array<string>;
  disabled?: boolean;
}

interface DateRow {
  date: string;
  endDate?: string;
}

const SKELETON_ROWS = 4;

export default function DepartureSelector({ tourOfferId, selected, onChange, savedDates, disabled }: Props) {
  const { data: departures = [], isLoading, error } = useGetTourOfferDepartures(tourOfferId);

  const rows = useMemo<Array<DateRow>>(() => {
    const byDate = new Map<string, DateRow>();
    departures.forEach((departure) => {
      if (!byDate.has(departure.startDate)) {
        byDate.set(departure.startDate, { date: departure.startDate, endDate: departure.endDate });
      }
    });
    (savedDates ?? []).forEach((date) => {
      if (!byDate.has(date)) {
        byDate.set(date, { date });
      }
    });
    return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
  }, [departures, savedDates]);

  const departingDates = useMemo(() => rows.filter((row) => row.endDate).map((row) => row.date), [rows]);
  const selectedDepartingCount = departingDates.filter((date) => selected.includes(date)).length;
  const isAllSelected = departingDates.length > 0 && selectedDepartingCount === departingDates.length;

  const toggle = (date: string) => {
    onChange(selected.includes(date) ? selected.filter((d) => d !== date) : [...selected, date]);
  };

  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  if (!isLoading && rows.length === 0) {
    return <Alert severity="info">This tour has no departures to sell.</Alert>;
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography variant="subtitle1" fontWeight="medium">
          Departures to sell
        </Typography>
        {!isLoading && (
          <Typography variant="body2" color="text.secondary">
            {selected.length} of {departingDates.length} selected
          </Typography>
        )}
      </Stack>

      <TableContainer sx={{ maxHeight: 360, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={selectedDepartingCount > 0 && !isAllSelected}
                  onChange={() => onChange(isAllSelected ? [] : departingDates)}
                  // Selecting before the departures load would select nothing and clear the current choice.
                  disabled={disabled || isLoading || departingDates.length === 0}
                  inputProps={{ 'aria-label': 'Select all departures' }}
                />
              </TableCell>
              <TableCell>Departs</TableCell>
              <TableCell>Returns</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading &&
              Array.from({ length: SKELETON_ROWS }, (_, index) => (
                <TableRow key={index}>
                  <TableCell padding="checkbox">
                    <Box p={1.5}>
                      <Skeleton variant="rectangular" width={18} height={18} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Skeleton width={120} />
                  </TableCell>
                  <TableCell>
                    <Skeleton width={120} />
                  </TableCell>
                </TableRow>
              ))}

            {!isLoading &&
              rows.map((row) => {
                const isSelected = selected.includes(row.date);
                return (
                  <TableRow
                    key={row.date}
                    hover={!disabled}
                    selected={isSelected}
                    onClick={disabled ? undefined : () => toggle(row.date)}
                    sx={{ cursor: disabled ? 'default' : 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        disabled={disabled}
                        inputProps={{ 'aria-label': `Sell departure ${row.date}` }}
                      />
                    </TableCell>
                    <TableCell>{formatDateWeekDayShortMonth(row.date)}</TableCell>
                    <TableCell>
                      {row.endDate ? (
                        formatDateWeekDayShortMonth(row.endDate)
                      ) : (
                        <Typography variant="body2" color="warning.main">
                          The tour no longer departs on this date
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
